import { useEffect, useState } from 'react';

/**
 * Hook to get theme-aware chart colors.
 * Returns an array of 10 colors that update when the theme changes.
 */
export function useChartColors(): string[] {
  const [colors, setColors] = useState<string[]>([
    '#ef4444', // fallback red
    '#f59e0b', // fallback amber
    '#10b981', // fallback emerald
    '#3b82f6', // fallback blue
    '#8b5cf6', // fallback violet
    '#ec4899', // fallback pink
    '#06B6D4', // fallback cyan
    '#84CC16', // fallback lime
    '#F97316', // fallback orange
    '#6366F1', // fallback indigo
  ]);

  useEffect(() => {
    const updateColors = () => {
      const root = document.documentElement;
      const styles = getComputedStyle(root);
      setColors([
        styles.getPropertyValue('--theme-chart-1').trim() || '#ef4444',
        styles.getPropertyValue('--theme-chart-2').trim() || '#f59e0b',
        styles.getPropertyValue('--theme-chart-3').trim() || '#10b981',
        styles.getPropertyValue('--theme-chart-4').trim() || '#3b82f6',
        styles.getPropertyValue('--theme-chart-5').trim() || '#8b5cf6',
        styles.getPropertyValue('--theme-chart-6').trim() || '#ec4899',
        styles.getPropertyValue('--theme-chart-7').trim() || '#06B6D4',
        styles.getPropertyValue('--theme-chart-8').trim() || '#84CC16',
        styles.getPropertyValue('--theme-chart-9').trim() || '#F97316',
        styles.getPropertyValue('--theme-chart-10').trim() || '#6366F1',
      ]);
    };

    // Initial update
    updateColors();

    // Watch for theme class changes on html element
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          updateColors();
          break;
        }
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  return colors;
}

/**
 * Get chart colors as an object with semantic names.
 */
export function useChartColorsObject(): Record<string, string> {
  const colors = useChartColors();
  return {
    chart1: colors[0],
    chart2: colors[1],
    chart3: colors[2],
    chart4: colors[3],
    chart5: colors[4],
    chart6: colors[5],
    chart7: colors[6],
    chart8: colors[7],
    chart9: colors[8],
    chart10: colors[9],
  };
}
